import React from 'react';

import { Formatters, Messages } from '~src/codegen/Localize';
import { useUsage } from '~src/spacesPlatform/hooks/useUsage';

import { KeyboardStep, KeyboardStepFast, MaxTreeViewportWidthFactor, MinTreeWidth, ProjectTreeService, resizableProjectTreeFeatureName } from '../services/ProjectTreeService';
import { projectTreeResizeGlobalDragStyle, projectTreeResizeHandleStyle } from './styles/ProjectTreeStyles';

export { projectTreeResizeHandleStyle } from './styles/ProjectTreeStyles';

interface ProjectTreeResizerProps {
    containerRef: React.RefObject<HTMLDivElement | null>;
    onWidthChange: (width: number) => void;
    onHoverChange?: (active: boolean) => void;
    onFocusChange?: (active: boolean) => void;
}

export function ProjectTreeResizer({ containerRef, onWidthChange, onHoverChange, onFocusChange }: ProjectTreeResizerProps): JSX.Element {
    const [isDragging, setIsDragging] = React.useState<boolean>(false);

    // get the layout elements we need to measure to calculate the max width
    const container = containerRef.current;
    const contentRow = container?.closest('.tb-row-flex-parent') as HTMLElement | null;
    const leftNav = contentRow?.querySelector('[data-tb-test-id="nav-panel"]') as HTMLElement | null;
    const initialViewportWidth = contentRow?.clientWidth ?? window.innerWidth;
    const initialLeftNavWidth = leftNav?.clientWidth ?? 0;

    const [viewportWidth, setViewportWidth] = React.useState<number>(initialViewportWidth);
    const [leftNavWidth, setLeftNavWidth] = React.useState<number>(initialLeftNavWidth);

    const maxWidth = (viewportWidth - leftNavWidth) * MaxTreeViewportWidthFactor;

    const [width, setWidth] = React.useState<number>(() => {
        const stored = ProjectTreeService.instance.loadStoredTreeWidth();
        return Math.min(Math.max(stored, MinTreeWidth), maxWidth);
    });

    // telemetry: sends `impression` on mount,
    // returns `click_through` sender for resize events (debounced for keyboard)
    const sendResizeTelemetry = useUsage(resizableProjectTreeFeatureName, {
        c: width,
        d: viewportWidth
    });

    // clamp and save the width to state and localStorage
    const saveWidth = (desiredWidth: number) => {
        const clamped = Math.min(Math.max(desiredWidth, MinTreeWidth), maxWidth);
        setWidth(prev => {
            if (clamped !== prev) {
                ProjectTreeService.instance.persistTreeWidth(clamped);
            }
            return clamped;
        });
    };

    // notify the parent component when width changes so it can resize itself
    React.useEffect(() => {
        onWidthChange(width);
    }, [width, onWidthChange]);

    // observe viewport and left nav width changes so we can calculate the max width
    React.useEffect(() => {
        if (!contentRow || typeof ResizeObserver === 'undefined') {
            return;
        }

        const viewportObserver = new ResizeObserver(entries => {
            if (entries[0]) {
                setViewportWidth(entries[0].contentRect.width);
            }
        });
        viewportObserver.observe(contentRow);

        let leftNavObserver: ResizeObserver | null = null;

        if (leftNav) {
            leftNavObserver = new ResizeObserver(entries => {
                if (entries[0]) {
                    setLeftNavWidth(entries[0].contentRect.width);
                }
            });
            leftNavObserver.observe(leftNav);
        }

        return () => {
            viewportObserver.disconnect();
            leftNavObserver?.disconnect();
        };
    }, [contentRow, leftNav]);

    // auto-reclamp when dimensions change
    React.useEffect(() => {
        saveWidth(width);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leftNavWidth, viewportWidth]);
    // we don't want width or saveWidth in deps as this would cause an infinite loop

    React.useEffect(() => {
        if (isDragging) {
            document.body.classList.add(projectTreeResizeGlobalDragStyle);
        } else {
            document.body.classList.remove(projectTreeResizeGlobalDragStyle);
        }

        // cleanup on unmount
        return () => {
            document.body.classList.remove(projectTreeResizeGlobalDragStyle);
        };
    }, [isDragging]);

    const createResizeCalculator = (startClientX: number) => {
        const initialWidth = width;
        return (clientX: number): number => {
            const delta = clientX - startClientX;
            const desiredWidth = initialWidth + delta;
            const clamped = Math.min(Math.max(desiredWidth, MinTreeWidth), maxWidth);
            saveWidth(desiredWidth);
            return clamped;
        };
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            const step = event.shiftKey ? KeyboardStepFast : KeyboardStep;
            const delta = event.key === 'ArrowLeft' ? -step : step;
            const desiredWidth = width + delta;
            const clampedWidth = Math.min(Math.max(desiredWidth, MinTreeWidth), maxWidth);
            saveWidth(desiredWidth);
            sendResizeTelemetry({ a: 'keyboard', c: clampedWidth, d: viewportWidth }, true);
            event.preventDefault();
        }
    };

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.button !== 0) {
            return;
        }

        setIsDragging(true);
        const calculateAndSaveWidth = createResizeCalculator(event.clientX);

        const handleMouseMove = (e: MouseEvent) => {
            calculateAndSaveWidth(e.clientX);
            e.preventDefault();
        };

        const handleMouseUp = (e: MouseEvent) => {
            setIsDragging(false);
            const finalWidth = calculateAndSaveWidth(e.clientX);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            onHoverChange?.(false);
            sendResizeTelemetry({ a: 'mouse', c: finalWidth, d: viewportWidth }, false);
            e.preventDefault();
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        onHoverChange?.(true);
        event.preventDefault();
    };

    const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
        if (event.touches.length === 0) {
            return;
        }

        setIsDragging(true);
        const initiatingTouch = event.touches[0];
        const touchIdentifier = initiatingTouch.identifier;
        const calculateAndSaveWidth = createResizeCalculator(initiatingTouch.clientX);

        // in case of multitouch, find the touch that started the drag
        const findMatchingTouch = (touchList: TouchList): Touch | undefined => {
            for (let i = 0; i < touchList.length; i++) {
                if (touchList[i].identifier === touchIdentifier) {
                    return touchList[i];
                }
            }
            return undefined;
        };

        const cleanup = () => {
            setIsDragging(false);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
            document.removeEventListener('touchcancel', handleTouchEnd);
            onHoverChange?.(false);
        };

        const handleTouchMove = (e: TouchEvent) => {
            const touch = findMatchingTouch(e.touches);
            if (touch) {
                calculateAndSaveWidth(touch.clientX);
                e.preventDefault();
            }
        };

        const handleTouchEnd = (e: TouchEvent) => {
            const touch = findMatchingTouch(e.changedTouches);
            if (touch) {
                const finalWidth = calculateAndSaveWidth(touch.clientX);
                sendResizeTelemetry({ a: 'touch', c: finalWidth, d: viewportWidth }, false);
                e.preventDefault();
            }
            cleanup();
        };

        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleTouchEnd);
        document.addEventListener('touchcancel', handleTouchEnd);

        onHoverChange?.(true);
        event.preventDefault();
    };

    const handleFocus = () => {
        onFocusChange?.(true);
    };

    const handleBlur = () => {
        onFocusChange?.(false);
    };

    const handleMouseEnter = () => {
        onHoverChange?.(true);
    };

    const handleMouseLeave = () => {
        if (!isDragging) {
            onHoverChange?.(false);
        }
    };

    return (
        <div
            aria-orientation='vertical'
            aria-valuenow={Math.round(width)}
            aria-valuemin={MinTreeWidth}
            aria-valuemax={Math.round(maxWidth)}
            aria-valuetext={Messages.projectTreeResizerAriaValueText({ WIDTH_LOC: Formatters.formatCount(Math.round(width)) })}
            aria-label={Messages.projectTreeResizerAriaLabel()}
            className={projectTreeResizeHandleStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            role='slider'
            tabIndex={0}
        />
    );
}
